import { useRef } from 'react';
import { useReduxState } from 'hooks';
import { AccessRequests } from 'components/common/accessRequest';
import { BoxTableCell } from 'components/common/box';
import { PathsInProduct } from 'components/common/pathsInContent';
import {
  CalendarEventParticipantsAdded,
  CalendarEventParticipantsAttributes,
  CalendarEventParticipantsEditCustomAttributes,
  CalendarEventParticipantsQueued
} from '.';

import type { CustomAttributes, User } from 'shared-types';
import type {
  AccessRequestActions,
  AccessRequestBody,
  AccessRequestHeader
} from 'components/common/accessRequest/AccessRequests';

type UserData = { userAttributes: CustomAttributes };

export type CalendarUser = User & {
  added: string;
  participated: boolean;
  participatedComment: string;
  status: 'added' | 'queued';
  userAttributes: CustomAttributes;
};

const CalendarEventParticipants = () => {
  const updateAddedUsersRef = useRef<(data?: any) => Promise<void>>(undefined);

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig
  } = useReduxState();

  const handleRequests = async (action: 'accept' | 'deny') => {
    if (action === 'accept') await updateAddedUsersRef.current();
  };

  const Header: AccessRequestHeader = () => (
    <>
      {calendarEvent.userAttributes?.length ? (
        <BoxTableCell>{messages.eventCalendarParticipantData}</BoxTableCell>
      ) : null}
    </>
  );

  const Body: AccessRequestBody<UserData> = ({ user, userData }) => (
    <>
      {calendarEvent.userAttributes?.length ? (
        <BoxTableCell>
          <CalendarEventParticipantsAttributes
            user={user}
            userAttributes={userData?.userAttributes || {}}
          />
        </BoxTableCell>
      ) : null}
    </>
  );

  const Actions: AccessRequestActions<UserData> = ({
    requestId,
    user,
    userData,
    update
  }) => (
    <CalendarEventParticipantsEditCustomAttributes
      requestId={requestId}
      updateUsers={update}
      user={{ ...user, userAttributes: userData?.userAttributes || {} }}
    />
  );

  return (
    <div>
      {calendarEvent?.access === 'request-access' ? (
        <div style={{ marginBottom: 40 }}>
          <AccessRequests
            customTableElements={{ Actions, Body, Header }}
            onRequestAction={handleRequests}
            url={`/admin/calendar-events/${calendarEvent.id}/request-access`}
          />
        </div>
      ) : null}

      {calendarEvent.numberOfParticipantsQueued ? (
        <div style={{ marginBottom: 40 }}>
          <CalendarEventParticipantsQueued
            updateAddedUsers={updateAddedUsersRef.current}
          />
        </div>
      ) : null}

      <div>
        <CalendarEventParticipantsAdded
          getUpdateData={(updateData) =>
            (updateAddedUsersRef.current = updateData)
          }
        />
      </div>

      {!serverConfig.hidePathsFromUI && (
        <div style={{ marginTop: 50 }}>
          <PathsInProduct
            tooltip={messages.eventCalendarEventPathTooltip}
            url={`/admin/calendar-events/${calendarEvent.id}/paths`}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarEventParticipants;
